import { Suspense, useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { AuthProvider } from '@/providers/AuthProvider'
import { ThemeProvider } from '@/providers/ThemeProvider'
import { LanguageProvider } from '@/providers/LanguageProvider'
import { InstallPrompt } from '@/components/pwa/InstallPrompt'
import { UpdateBanner } from '@/components/UpdateBanner'
import { useTenantAppIdentity } from '@/hooks/useTenantAppIdentity'
import { useTranslation } from 'react-i18next'
import { router } from '@/routes'
import { recoverChunkLoadError } from '@/lib/chunk-recovery'

function LoadingFallback() {
  return (
    // The page colour and nothing else: a «...» here was one more placeholder in the sequence
    // David recorded (24.10.2026); the route's own skeleton follows within a frame.
    <div className="h-full bg-[var(--surface-page)]" />
  )
}

export default function App() {
  const { t } = useTranslation()
  // The signed-in viewer's tenant owns the app they install, on every route —
  // not only on the token surface `usePublicEventBrand` covers. Mounted here
  // rather than in AppLayout because `/event/:id` and the public routes render
  // outside that shell, and the manifest link is a document-level fact. Reads
  // the auth store directly (Zustand, not context), so its position relative to
  // AuthProvider below does not matter. Publishes nothing and renders nothing
  // when the viewer has no tenant.
  useTenantAppIdentity()
  // Uncaught imports share the same guard as both React error boundaries.
  useEffect(() => {
    const handler = (event: ErrorEvent | PromiseRejectionEvent) => {
      recoverChunkLoadError('reason' in event ? event.reason : event.error)
    }
    window.addEventListener('error', handler)
    window.addEventListener('unhandledrejection', handler)
    return () => {
      window.removeEventListener('error', handler)
      window.removeEventListener('unhandledrejection', handler)
    }
  }, [])

  return (
    <ErrorBoundary>
      {/* Skip-link (WCAG 2.4.1): kept off-screen above the viewport, slides in
          on keyboard focus. Off-screen-position pattern (not sr-only) so it
          reliably reveals — Tailwind's not-sr-only width revert was a no-op. */}
      <a
        href="#main-content"
        className="print:hidden fixed left-2 -top-16 focus:top-2 z-[var(--z-modal)] rounded-[var(--radius-md)] bg-[var(--action-primary-bg)] px-4 py-2 text-sm font-medium text-[var(--action-primary-text)] no-underline shadow-[var(--shadow-lg)] transition-all"
      >
        {t('common.skipToContent', { defaultValue: 'Hopp til innhold' })}
      </a>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <Suspense fallback={<LoadingFallback />}>
              <RouterProvider router={router} />
            </Suspense>
            <UpdateBanner />
            <InstallPrompt />
            <Toaster
              position="bottom-right"
              // Clear the bottom bars (app nav / event tab bar / sticky CTA) —
              // sonner's default 16px parked every toast on top of them at
              // mobile widths, covering the middle tab slots. Desktop reads the
              // same inset (K14, 22.09.2026): a pinned editor footer is bottom
              // chrome at every width, and a save toast covered its «Gå til
              // publisering». With nothing reported both stay sonner's defaults.
              offset={{ bottom: 'calc(var(--bottom-chrome-inset, 0px) + 24px)' }}
              mobileOffset={{ bottom: 'calc(var(--bottom-chrome-inset, 0px) + 16px)' }}
              toastOptions={{
                style: {
                  borderRadius: 'var(--toast-radius)',
                  boxShadow: 'var(--toast-shadow)',
                  padding: 'var(--toast-padding)',
                  background: 'var(--surface-elevated)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)',
                },
              }}
            />
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
